import type { UIDebugPageLink } from '@sorrem-client/types';
import { useGetAuthSessionQuery } from '@sorrem-client/ui/store';

/**
 * The developer pages (/debug/* and /lore), in menu order. AppRoutes maps
 * over this list, so a page added here must be given a route there. The
 * editors (/editors/*) are their own set, open to creators too (see
 * use-editor-access.ts).
 */
export const DEBUG_PAGES = [
    { path: '/debug/maps', label: 'Map Debugger', tabLabel: 'Map' },
    { path: '/debug/overlay', label: 'Overlay Debugger', tabLabel: 'Game Overlay' },
    { path: '/debug/characters', label: 'Character Animation Debugger', tabLabel: 'Character Animation' },
    { path: '/debug/vfx', label: 'VFX Debugger', tabLabel: 'VFX' },
    { path: '/debug/sounds', label: 'Sound Debugger', tabLabel: 'Sound' },
    { path: '/lore', label: 'Lore', tabLabel: 'Lore' },
] as const satisfies readonly UIDebugPageLink[];

/** A developer page's route path. */
export type DebugPagePath = typeof DEBUG_PAGES[number]['path'];

const NO_DEBUG_PAGES: readonly UIDebugPageLink[] = [];

export interface DeveloperAccessState {
    /** Whether the current session holds the developer role. False while loading. */
    isDeveloper: boolean;
    /** Debug pages this session may open; empty unless `isDeveloper`. */
    debugPages: readonly UIDebugPageLink[];
}

/**
 * The current session's developer role (the `isDeveloper` claim from
 * GET /api/auth/session) and the debug pages it unlocks.
 */
export function useDeveloperAccess(): DeveloperAccessState {
    const { data } = useGetAuthSessionQuery();
    const isDeveloper = data?.isDeveloper ?? false;

    return {
        isDeveloper,
        debugPages: isDeveloper ? DEBUG_PAGES : NO_DEBUG_PAGES,
    };
}
